import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ModernHeader from '../../../components/layout/Headers/ModernHeader';
import ModernFootermenu from '../../../components/layout/Footer';
import { copyTextToClipboard } from '../../../shared/utils/function';
import { ROUTES } from '../../../shared/constants/routes';
import './webview.css';

const isHttpUrl = (value) => {
  if (!value || typeof value !== 'string') return false;
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch (e) {
    return false;
  }
};

const WebView = () => {
  const location = useLocation();
  const pageRef = useRef(null);
  const [hasCopied, setHasCopied] = useState(false);
  const [isFrameLoading, setIsFrameLoading] = useState(false);

  const { url, title } = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const rawUrl = params.get('url') || '';
    const rawTitle = params.get('title') || '';
    return {
      url: rawUrl,
      title: rawTitle,
    };
  }, [location.search]);

  const isValid = useMemo(() => isHttpUrl(url), [url]);

  useEffect(() => {
    setHasCopied(false);
    setIsFrameLoading(Boolean(isValid));
  }, [isValid, url]);

  const handleCopy = async () => {
    if (!url) return;
    const didCopy = await copyTextToClipboard(url, 'Copy this link:');
    if (didCopy) {
      setHasCopied(true);
      window.setTimeout(() => setHasCopied(false), 2000);
    }
  };

  const handleOpenExternal = () => {
    if (!url) return;
    // Prefer a new tab so users can continue if the site blocks embedding.
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (w) w.opener = null;
  };

  // The header and footer are fixed-positioned (removed from normal flow).
  // Measure their heights so the iframe can fill the remaining visible space.
  useLayoutEffect(() => {
    const root = pageRef.current;
    if (!root) return;

    const getHeaderEl = () => root.querySelector('header.cm_header');
    const getFooterEl = () => root.querySelector('footer.modern-footer');

    let ro = null;

    const applyHeights = () => {
      const headerEl = getHeaderEl();
      const footerEl = getFooterEl();
      const headerH = headerEl?.getBoundingClientRect?.().height || 0;
      const footerH = footerEl?.getBoundingClientRect?.().height || 0;

      root.style.setProperty('--cm-webview-header-height', `${Math.ceil(headerH)}px`);
      root.style.setProperty('--cm-webview-footer-height', `${Math.ceil(footerH)}px`);
    };

    applyHeights();

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => applyHeights());
      const headerEl = getHeaderEl();
      const footerEl = getFooterEl();
      if (headerEl) ro.observe(headerEl);
      if (footerEl) ro.observe(footerEl);
    }

    window.addEventListener('resize', applyHeights);
    return () => {
      window.removeEventListener('resize', applyHeights);
      ro?.disconnect?.();
    };
  }, []);

  return (
    <div
      ref={pageRef}
      className="modern-webview-page"
    >
      <ModernHeader
        customTitle={title || 'Web View'}
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
        backButtonUrl={
          title === 'W-9 Tax Sign Form'
            ? ROUTES.W9_TAX_INFO
            : title === 'W-8 Tax Sign Form'
            ? ROUTES.W8_TAX_INFO
            : null
        }
      />

      <main
        style={{
          position: 'fixed',
          top: 'var(--cm-webview-header-height, 66px)',
          bottom: 'var(--cm-webview-footer-height, 0px)',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '479px',
          overflow: 'hidden',
        }}
      >
        {!isValid ? (
          <div className="p-4">
            <h4 className="mb-2">Unable to load this page</h4>
            <p className="mb-3">
              The link is missing or invalid. Please go back and try again.
            </p>
          </div>
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* <div className="modern-webview-toolbar">
              <div className="modern-webview-toolbar__left">
                <div className="modern-webview-toolbar__title">{title || 'Web View'}</div>
                <div className="modern-webview-toolbar__url">{url}</div>
              </div>
              <div className="modern-webview-toolbar__actions">
                <button
                  type="button"
                  className="modern-webview-toolbar__btn"
                  onClick={handleOpenExternal}
                >
                  Open
                </button>
                <button
                  type="button"
                  className="modern-webview-toolbar__btn"
                  onClick={handleCopy}
                >
                  {hasCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div> */}

            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
              {isFrameLoading && (
                <div className="modern-webview-loading" aria-live="polite">
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  <span>Loading…</span>
                </div>
              )}
              <iframe
                src={url}
                title={title || 'Embedded Content'}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                loading="eager"
                allow="clipboard-read; clipboard-write; fullscreen"
                onLoad={() => setIsFrameLoading(false)}
              />
            </div>
          </div>
        )}
      </main>

      <ModernFootermenu />
    </div>
  );
};

export default WebView;

